import { ChangeDetectorRef, Component, EventEmitter, inject, OnInit, Output, ViewChild, AfterViewInit, OnDestroy, ElementRef, Input } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ManageReportService } from 'src/app/features/reports/services/manage.service';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { DataCopilotApiService } from 'src/app/core/http/services/data-copilot.service';
import { ReportsApiService } from 'src/app/core/http/services/reports-service';
import { PostHogService } from '../../services/post-hog.service';
import { CopilotHelperServiceService } from '../../services/copilot-helper-service.service';
import { ExportService } from '../../services/export.service';
import { CopilotStorageService } from '../../services/copilot-storage.service';

import { UIIconsComponent } from '../../ui/icons/icons.component';
import { LoaderComponent } from '../../common-ui-components/loader/loader.component';
import { AgentTriggerComponent } from "../../common-ui-components/agent-trigger/agent-trigger.component";
import { ReportDataselectorComponent } from '../report-dataselector/report-dataselector.component';
import { ReportActionsComponent } from '../report-actions/report-actions.component';
import { ReportVisualDataComponent } from '../report-visual-data/report-visual-data.component';

import { ReportFilter } from 'src/app/core/models/report-filter.model';
import { FILTER_OPERATORS, ReportNoDataMessages } from '../../constants/report-shell.constant';
import { FilterOperatorKey } from '../../enum/report-filter.enum';
import { DATE_TIME_FORMAT } from '../../constants/date-time-format.constant';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { cloneDeep } from '@apollo/client/utilities';
import isEqual from 'lodash/isEqual';
import { MarkdownModule } from 'ngx-markdown';
import hljs from 'highlight.js';
import { format } from "sql-formatter";
import { SqlPlaygroundModalComponent } from '../../components/sql-playground-modal/sql-playground-modal.component';

@Component({
  selector: 'app-report-framework',
  standalone: true,
  imports: [
    UIIconsComponent,
    CommonModule,
    FormsModule,
    ReportVisualDataComponent,
    ReportActionsComponent,
    LoaderComponent,
    AgentTriggerComponent,
    ReportDataselectorComponent,
    MarkdownModule,
    SqlPlaygroundModalComponent
],
providers: [DatePipe],
  templateUrl: './report-framework.component.html',
  styleUrl: './report-framework.component.css',
})
export class ReportFrameworkComponent implements OnInit, AfterViewInit, OnDestroy {
  // Injected Services
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private manageReportService = inject(ManageReportService);
  private reportsApiService = inject(ReportsApiService);
  private dataCopilotService = inject(DataCopilotApiService);
  private assetsApiService = inject(AssetsApiService);
  private copilotHelperService = inject(CopilotHelperServiceService);
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);
  private postHogService = inject(PostHogService);
  private copilotStorageService = inject(CopilotStorageService);
  /**
   * Subject used with takeUntil to automatically unsubscribe from observables when the component
   * is destroyed. Prevents memory leaks when navigating away from the report (subscription would
   * otherwise persist and could cause errors if it emits after the component is torn down).
   */
  private readonly destroy$ = new Subject<void>();
  @Input() reportId: string = '';
  @Input() isCanvas: boolean = false;
  @Input() cardId?: string;
  @Input() canvasVisualType?: 'TABLE' | 'CHART';
  @Input() canvasChartConfigurations?: any;
  @Input() canvasGridConfigurations?: any;
  
  // UI Properties
  public modalWidth: number = 350;
  public resizing: boolean = false;
  public initialX: number = 0;
  public initialWidth: number = 0;
  public actionName: string = '';
  public isQuickModalOpen: boolean = false;
  public selectedAction: any;
  public showConfirmationPopup = false;
  public confirmationPopupMsg = '';
  public confirmationPopupHeader = '';
  public confirmationCallbacks = {
    confirmAction: () => {
      // Will be set dynamically based on context
    },
    cancelAction: () => {
      this.closeConfirmationPopUp();
    }
  }
  public isSelectScopeOpen: boolean = false;
  public searchScopeTerm: any;
  public searchTerm: string = '';
  public showInsights: boolean = false;
  
  // Report Data Properties  
  public reportsHeading = 'reportsHeading';
  public reportsDescription = 'reportsDescription';
  public reportTemplate: any;
  public selectedScope: any;
  public isSelectedScope: boolean = false;
  public templateTypesLoading: boolean = false;
  @Input()  reportVisualType: any = 'CHART';
  public reportVisualData: any = [];
  public reportVisualDataLoading: boolean = false;
  public reportVisualDataEmptyState: boolean = true;
  public isSavedReport: boolean = false;
  private reportTemplateId: string = '';
  public isValidSelection: boolean = false;
  public selectedBusinessTerm = 1;
  public isReportTemplateDataLoading: boolean = false;
  public loadingDatascopes:boolean = false;
  
  // Column and Data Selection Properties
  public savedColumnIds: string[] = [];
  public sqlFunctionMap: { [columnId: string]: string } = {};
  public selectedTableIds: string[] = [];
  public checkedColumns: any[] = [];
  public attributes: any = [];
  public measures: any = [];
  
  // Filter Properties
  public appliedFilters: ReportFilter[] = [];
  public filterJoinOperators = ['AND', 'OR'];
  public filterOperators = FILTER_OPERATORS;
  public filterOperatorKeys = FilterOperatorKey;
  public reportDataEmptyStateMessage = ReportNoDataMessages.SelectScopeAndRun;
  public reportDataError = false;
  
  // Date Format Properties
  public dateTimeFormat = DATE_TIME_FORMAT;
  public selectedDateTimeFormat = '';
  public appliedDateTimeFormat = '';
  public customDateFormat: string = 'dd MMM yyyy';

  // AG Grid Chart Properties
  public agGridChartType: string = 'bar';
  public agGridChartModel: any = null;
  public gridFilterModel: any = null;
  public gridSortModel: any = null;

  // State Management
  public savedReportState: any = null;
  public currentReportState: any = null;
  public isNewState: boolean = false;
  public isRunDisabled: boolean = false;

  // AI and Insights
  public loadingInsights: boolean = false;
  public insights: any[] = [];
  private insightsRequested: boolean = false;
  public conversations: any = {
    questions: [],
    answers: []
  };
  public answers: {
    isLoading: boolean;
    text: string;
    type: String;
    timestamp: any;
    tableData?: any;
    warning?: boolean;
    insights?: string | null;
    showInsights?: boolean;
    loadingInsights?: boolean;
  }[] = [];
  public questions: any[] = [];
  
  // Quick Actions Configuration
  public quickActions = [
    {
      name: 'refresh',
      strokeWidth: '1.5px',
      clickAction: 'REFRESH',
      label: 'refresh',
    },
    {
      name: 'lightbulb',
      strokeWidth: '2px',
      clickAction: 'INSIGHTS',
      label: 'AI Generated Insights',
    },
    {
      name: 'data-copilot',
      strokeWidth: '2px',
      clickAction: 'COPILOT',
      label: 'Speak with Copilot',
    },
    {
      name: 'download',
      strokeWidth: '2px',
      clickAction: 'SHARE',
      label: 'Export',
    },
    {
      name: 'save',
      strokeWidth: '2px',
      clickAction: 'SAVE',
      label: 'Save report',
    },
  ];
  
  // Resize event listeners
  resizeListenerBound: any;
  stopResizeListenerBound: any;

   // SQL modals
   public showSqlModal: boolean = false;
   public isSqlEditable: boolean = false;
   public isRunSqlLoading: boolean = false;
   public formattedSqlHtml: string = '';
   public initialSql: string = '';
   isSqlErrorResponse = false;
   public generatedSql = '';
   private _lastExecutionPayload: string | null = null;
  
  // View References
  @ViewChild('reportDataselector') reportDataselector!: ReportDataselectorComponent;
  @ViewChild(ReportVisualDataComponent) reportVisualDataComponent!: ReportVisualDataComponent;

  // Output Events
  @Output() templateTypesLoadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() handleIsUseTemplateReport: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() reportNameEmitter: EventEmitter<{name: string, description: string}> = new EventEmitter<{name: string, description: string}>();
  @Output() reportDataLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() reportLoadError: EventEmitter<string> = new EventEmitter<string>();
  @Output() insightsGenerated: EventEmitter<{reportName: string, insights: string}> = new EventEmitter<{reportName: string, insights: string}>();
  @Output() insightsError: EventEmitter<string> = new EventEmitter<string>();
  @Output() visualTypeLoaded: EventEmitter<'TABLE' | 'CHART'> = new EventEmitter<'TABLE' | 'CHART'>();
  @Output() chartConfigurationsChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() gridConfigurationsChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(private cdr: ChangeDetectorRef,
    private el: ElementRef) {}

  // Lifecycle Methods
  ngOnInit(): void {
    if (this.selectedScope) {
      this.isSelectedScope = true;
    }
    this.appliedDateTimeFormat = this.dateTimeFormat[0].value;
    
    if ((this.router.url.includes('/dashboard/reports/saved/single')) || (this.isCanvas && this.reportId)) {
      this.isSavedReport = true;
      if (!this.isCanvas) {
        this.reportId = this.route.snapshot.queryParams['id'];
      }
    }
    
    this.isReportTemplateDataLoading = true;
    this.getReportTemplate();

     // Use canvasVisualType if provided, otherwise use report's visual type
     this.reportVisualType = this.canvasVisualType || this.reportTemplate?.parameters?.reportVisualType || 'CHART';
    // When user selects Chart/Both in export modal, this switches the report view - must unsubscribe on destroy
    this.exportService.$reportExportFormatTypeChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((type: string) => this.changeReportVisualType(type));
    this.updateCurrentState(); 
  }

  ngAfterViewInit(): void {
    if (this.isSavedReport && this.reportDataselector) {
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 300);
    }

    document.querySelectorAll('pre code').forEach((block) => {
      hljs.highlightElement(block as HTMLElement);
    });
    const blockCodes = this.el.nativeElement.querySelectorAll('pre code');
    const inlineCodes = this.el.nativeElement.querySelectorAll('p code');

    blockCodes.forEach((block: HTMLElement) => hljs.highlightElement(block));
  }

  ngOnDestroy(): void {
    // Emit and complete to trigger takeUntil on all subscriptions - ensures clean teardown
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Template Loading Methods
  public getReportTemplate(): void {
    if (this.isCanvas && this.reportId) {
      this.assetsApiService.getAssetById(this.reportId).subscribe({
        next: ({ data }) => this.handleFetchedAsset(data.asset),
        error: (err) => this.handleFetchError(err),
      });
    } else {
      this.route.queryParams.subscribe({
        next: (params) => {
          this.assetsApiService.getAssetById(params['id']).subscribe({
            next: ({ data }) => this.handleFetchedAsset(data.asset),
            error: (err) => this.handleFetchError(err),
          });
        },
      });
    }
  }
  
  private handleFetchedAsset(asset: any): void {
    this.reportTemplateId = asset.id;

    if (this.isSavedReport) {
      this.selectedScope = {
        id: asset.parameters.datascopeId,
        name: asset.parameters.dataScopeName,
        connectionId: asset.parameters.connectionId,
      };
      this.isSelectedScope = true;
      this.reportTemplateId = asset.parameters.templateId;
       // Use canvasVisualType if provided, otherwise use report's visual type
       this.reportVisualType = this.canvasVisualType || asset.parameters.reportVisualType || 'CHART';
      this.savedColumnIds = asset.parameters.columnIds || [];
      this.selectedTableIds = asset.parameters.tableIds || [];
      this.sqlFunctionMap = { ...(asset.parameters.sqlFunctionMap || {}) };
      this.insights = asset.parameters.INSIGHTS || [];
      const savedConversations = this.copilotStorageService.loadConversations(asset.id);
      if (savedConversations) {
        this.conversations = savedConversations;
      } else {
        this.conversations = {
          questions: [],
          answers: []
        };
      }

      if (this.canvasChartConfigurations?.chartModel) {
        this.agGridChartModel = this.canvasChartConfigurations.chartModel;
        if (this.canvasChartConfigurations.chartType) {
          this.agGridChartType = this.canvasChartConfigurations.chartType;
        }
      } else {
        if (asset.parameters.agGridChartModel) {
          this.agGridChartModel = asset.parameters.agGridChartModel;
        }
        if (asset.parameters.agGridChartType) {
          this.agGridChartType = asset.parameters.agGridChartType;
        }
      }

      if (this.canvasGridConfigurations) {
        this.gridFilterModel = this.canvasGridConfigurations.filterModel || null;
        this.gridSortModel = this.canvasGridConfigurations.sortModel || null;
      } else {
        this.gridFilterModel = asset.parameters.gridFilterModel || null;
        this.gridSortModel = asset.parameters.gridSortModel || null;
      }
    }
  
    this.reportTemplate = asset;
    this.manageReportService.setState({ reportTemplate: asset });
     // Use canvasVisualType if provided, otherwise use report's visual type
     this.reportVisualType = this.canvasVisualType || asset?.parameters?.reportVisualType || 'CHART';
    // Emit the visual type so parent component (card) can sync
    // Only emit if canvasVisualType is not provided (to avoid circular updates)
    if (this.isCanvas && !this.canvasVisualType) {
      this.visualTypeLoaded.emit(this.reportVisualType);
    }
    
    this.reportNameEmitter.emit({
      name: asset.name,
      description: asset.description || ''
    });
    if (asset.parameters?.appliedFilters) {
      this.appliedFilters = cloneDeep(asset.parameters.appliedFilters);
    } else {
      this.appliedFilters = [];
    }
    
    this.isReportTemplateDataLoading = false;
    
    if ( this.isSavedReport) {
      setTimeout(() => {
        if (this.reportDataselector) {
          console.log('Initializing tables for saved report');
          this.reportDataselector.initializeTablesForSavedReport();
        } else {
          console.error('reportDataselector is still undefined after timeout');
          // Fallback approach for when child component isn't available
          this.initializeFallback(asset);
        }
      }, 0);
    } else {
      this.executeReportData({
        checkedColumns: this.checkedColumns,
        sqlFunctionMap: asset.parameters.sqlFunctionMap || {}
      });
    }
  }  

  private initializeFallback(asset: any): void {
    // Execute report with minimal data
    this.executeReportData({
      checkedColumns: this.savedColumnIds.map(id => ({ id, checked: true })),
      sqlFunctionMap: this.sqlFunctionMap
    });
  }

  private handleFetchError(error: any): void {
    console.error('Error fetching report template:', error);
    this.isReportTemplateDataLoading = false;
    this.reportLoadError.emit('Error loading report template');
  }

  // UI Action Methods
  public getIconClasses(action: any): any {
    return {
      'stroke-[#344054]': true,
      'hover:stroke-[#0F49A0]': true,
      [`stroke-[${action.strokeWidth}px]`]: true,
    };
  }

  public onQuickActionClick(action: any): void {
    this.postHogService.capture(`REPORT SHELL: ${action.label} TOOLBAR CLICKED`);
    this.selectedAction = action;

    if (!this.isQuickModalOpen && action.clickAction !== 'REFRESH') {
      if (!this.currentReportState) {
        this.updateCurrentState();
      }

      if(this.reportDataselector){
        this.reportDataselector?.toggleCollapse(true);
      }

    }

    if (action.clickAction === 'SETTING') {
      this.savedReportState = null;
    }

    if (action.clickAction !== 'REFRESH') {
      if ((this.selectedAction?.clickAction === 'SETTING' || this.selectedAction?.clickAction === 'COPILOT') && this.modalWidth < 450) {
        this.modalWidth = 450;
      }
      this.isQuickModalOpen = this.actionName === action.clickAction ? false : true;
      this.actionName = this.isQuickModalOpen ? action.clickAction: '';
    } else {
      this.isQuickModalOpen = false;
      if (this.selectedScope) {
        this.reloadReport();
      }
    }
  }  

  public closeQuickActionModal(): void {
    this.isQuickModalOpen = false;
    this.actionName = '';

    if (this.reportDataselector && this.reportDataselector.isDataPickerCollapsed) {
      this.reportDataselector?.toggleCollapse(false);
    }
  }

  public reloadReport(): void {
    this.postHogService.capture('REPORT SHELL: RELOAD REPORT');

    if (this.isSavedReport) {
      this.reloadSavedReport();
    } else {
      this.reloadUnsavedReport();
    }
  }

  private reloadSavedReport(): void {
    const asset = this.reportTemplate;

    if (!asset || !asset.parameters) {
      console.error('Cannot reload: No report template available');
      return;
    }

    this._lastExecutionPayload = null;

    this.savedColumnIds = asset.parameters.columnIds || [];
    this.selectedTableIds = asset.parameters.tableIds || [];
    this.sqlFunctionMap = { ...(asset.parameters.sqlFunctionMap || {}) };
    this.appliedFilters = asset.parameters.appliedFilters ? cloneDeep(asset.parameters.appliedFilters) : [];
    this.reportVisualType = asset.parameters.reportVisualType || 'CHART';

    // Load AG Grid chart configuration
    if (asset.parameters.agGridChartModel) {
      this.agGridChartModel = asset.parameters.agGridChartModel;
    }
    if (asset.parameters.agGridChartType) {
      this.agGridChartType = asset.parameters.agGridChartType;
    }

    if (this.reportDataselector) {
      this.reportDataselector.savedColumnIds = [...this.savedColumnIds];
      this.reportDataselector.initialSqlFunctionMap = { ...this.sqlFunctionMap };
      this.reportDataselector.appliedFilters = cloneDeep(this.appliedFilters);
      this.reportDataselector.selectedTableIds = [...this.selectedTableIds];

      setTimeout(() => {
        if (this.reportDataselector) {
          console.log('Re-initializing tables for saved report reload');
          this.reportDataselector.initializeTablesForSavedReport();
        }
      }, 0);
    }
  }

  private reloadUnsavedReport(): void {
    this._lastExecutionPayload = null;

    this.sqlFunctionMap = {};
    this.appliedFilters = [];
    this.savedColumnIds = [];
    this.checkedColumns = [];
    this.selectedTableIds = [];
    this.reportVisualData = [];
    this.attributes = [];
    this.measures = [];
    this.agGridChartModel = null;
    this.agGridChartType = 'bar';

    if (this.reportDataselector) {
      this.reportDataselector.checkedColumns = [];
      this.reportDataselector.sqlFunctionMap = {};
      this.reportDataselector.selectedFilters = [];
      this.reportDataselector.savedColumnIds = [];
      this.reportDataselector.isValidSelection = false;

      [this.reportDataselector.selectedTablesForMeasures, this.reportDataselector.selectedTablesForAttributes].forEach(tables => {
        tables?.forEach((table: any) => {
          if (table.columns) {
            table.columns.forEach((column: any) => {
              column.checked = false;
            });
          }
        });
      });
    }

    this.setNoDataReportMessage(true, ReportNoDataMessages.SelectScopeAndRun);
    this.updateCurrentState();
    this.cdr.detectChanges();
  }

  public closeConfirmationPopUp(): void {
    this.showConfirmationPopup = false;
    this.confirmationPopupMsg = '';
    this.confirmationPopupHeader = '';
  }

  public onClickBack(): void {
    this.router.navigate(['/dashboard/reports']);
  }

  // Report Execution Methods
  public executeReportData(reportData?: { checkedColumns: any, sqlFunctionMap: any }, forceExecution: boolean = false): void {
    const { tableIds, columnIds, sqlFuncMap } = reportData ? this.prepareReportExecutionData(reportData) : {tableIds: this.selectedTableIds,columnIds: this.savedColumnIds, sqlFuncMap: this.sqlFunctionMap};

    const payloadKey = JSON.stringify({
      scopeId: this.selectedScope?.id,
      templateId: this.reportTemplateId,
      tableIds,
      columnIds,
      sqlFunctionMap: sqlFuncMap,
      filters: this.appliedFilters
    });

    if (!forceExecution && payloadKey === this._lastExecutionPayload) {
      return;
    }

    this.postHogService.capture('REPORT SHELL: RUN REPORT');
    this.closeQuickActionModal();

    if (this.reportVisualDataComponent) {
      this.reportVisualDataComponent.clearFiltersAndSorts();
    }

    this.reportVisualDataLoading = true;
    this.setNoDataReportMessage(false);

    if (!this.selectedScope || !columnIds.length) {
      this.reportVisualDataLoading = false;
      this.setNoDataReportMessage(true);
      return;
    }

    const payload = {
      connectionId: this.selectedScope.connectionId,
      datascopeId: this.selectedScope.id,
      templateId: this.reportTemplateId,
      tableIds: tableIds.length > 0 ? tableIds : this.selectedTableIds,
      columnIds,
      sqlFunctionMap: sqlFuncMap,
      filters: this.prepareFilterPayload(this.appliedFilters),
    };

    this.savedColumnIds = [...columnIds as string[]];

    this.reportsApiService.reportExecution(payload).subscribe({
      next: (data) => this.handleReportExecutionSuccess(data, sqlFuncMap, payloadKey),
      error: (error) => this.handleReportExecutionError(error)
    });
  }

  

  private prepareReportExecutionData(reportData?: { checkedColumns: any, sqlFunctionMap: any }): {tableIds: string[], columnIds: string[], sqlFuncMap: any} {
    let tableIds = Array.from(new Set(reportData?.checkedColumns.map((column: any) => column.tableId))).filter(Boolean) as string[] ?? this.selectedTableIds;
    const columnIds = Array.from(new Set(reportData?.checkedColumns.map((column: any) => column.id)) ?? this.getSelectedColumnIds().map((column) => column.id)) as string[];
    const sqlFuncMap = reportData ? reportData.sqlFunctionMap : this.sqlFunctionMap;
    return {tableIds, columnIds, sqlFuncMap};
  }

  private handleReportExecutionSuccess(data: any, sqlFuncMap: any, payloadKey: string): void {
    this._lastExecutionPayload = payloadKey;
    this.reportVisualData = data?.questionAssets?.length > 0 ? data.questionAssets[0]?.parameters?.DATA : [];
    this.generatedSql = data?.questionAssets?.length > 0 ? data.questionAssets[0]?.parameters?.SECURED_SQL : '';
    const isVisualDataEmpty = this.reportVisualData.length === 0;
    this.setNoDataReportMessage(
      isVisualDataEmpty,
      (isVisualDataEmpty && this.appliedFilters.length > 0) ?
        ReportNoDataMessages.ErrorFilterNoResult :
        ReportNoDataMessages.SelectScopeAndRun
    );
    const insights = data.questionAssets[0]?.parameters?.INSIGHTS;
    if(insights){
      this.insights = [{content:insights, timestamp: new Date() }];
      this.currentReportState.insights = this.insights;
    }

    this.reportVisualDataEmptyState = this.reportVisualData.length === 0;
    this.reportVisualDataLoading = false;
    this.extractMeasuresAndAttributes(this.reportVisualData);
    this.sqlFunctionMap = sqlFuncMap;
    this.isNewState = false;
    this.isRunDisabled = true;
    this.reportDataLoaded.emit(true);
    this.cdr.detectChanges();
  }

  private handleReportExecutionError(error: any): void {
    console.error('Error executing report data:', error);
    this.reportVisualDataLoading = false;
    const errMsg =
      (error.error && error.error.message) ||
      'Failed to load report — please try again.';
    this.setNoDataReportMessage(true, errMsg, true);
    this.reportLoadError.emit(errMsg);
  }

  // Data Selection and Processing Methods
  public onScopeSelected(scope: any): void {
    this.selectedScope = scope;
    this.manageReportService.setState({ selectedScope: scope });
    this.isSelectedScope = true;
    this.reportVisualData = [];
    this.setNoDataReportMessage(true);
    this.attributes = [];
    this.measures = [];
    this.appliedFilters = [];
    this.agGridChartModel = null;
    this.agGridChartType = 'bar';
    this.sqlFunctionMap = {};
    this.savedColumnIds = [];
    this.checkedColumns = [];
    this.selectedTableIds = [];
    this.isRunDisabled = false;
    this.resetReportState();
    this.resetInsights();
    this.closeQuickActionModal();
  }
  
  public onApplyFilters(filters: any[]): void {
    this.appliedFilters = cloneDeep(filters);
    this.isRunDisabled = false;
    this.updateCurrentState();
    
    if (this.isSelectedScope && this.isValidSelection) {
      this.executeReportData();
    }
  }

  public onRunReport(reportData: { checkedColumns: any, sqlFunctionMap: any }): void {
    this.executeReportData(reportData, false);
  }
  
  public onCancelReport(): void {
    this.postHogService.capture('REPORT SHELL: CANCEL REPORT');
    if (this.savedReportState) {
      this.sqlFunctionMap = cloneDeep(this.savedReportState.sqlFunctionMap);
      this.appliedFilters = cloneDeep(this.savedReportState.filters);
      if (this.savedReportState.agGridChartModel) {
        this.agGridChartModel = cloneDeep(this.savedReportState.agGridChartModel);
      }
      if (this.savedReportState.agGridChartType) {
        this.agGridChartType = this.savedReportState.agGridChartType;
      }
      this.updateCurrentState();
      this.cdr.detectChanges();
    } else {
      this.reportVisualData = [];
      this.sqlFunctionMap = {};
      this.appliedFilters = [];
      this.attributes = [];
      this.measures = [];
      this.reportVisualDataLoading = false;
      this.setNoDataReportMessage(true);
      this.updateCurrentState();
    }
  }
  
  public onSqlFunctionApplied(event: {columnId: string, functionName: string}): void {
    if (event.functionName) {
      this.sqlFunctionMap[event.columnId] = event.functionName;
    } else {
      delete this.sqlFunctionMap[event.columnId];
    }
    this.isRunDisabled = false;
    this.updateCurrentState();
  }
  
  public extractMeasuresAndAttributes(dataArray: any) {
    this.attributes = [];
    this.measures = [];
    const keysSet = new Set<string>();
    const measuresSet = new Set<string>();

    if (dataArray.length > 0) {
      dataArray.forEach(
        (item: { [s: string]: unknown } | ArrayLike<unknown>) => {
          Object.entries(item).forEach(([key, value]) => {
            if (typeof value === 'string' && !keysSet.has(key)) {
              keysSet.add(key);
              this.attributes.push({ id: key, name: key });
            } else if (typeof value === 'number' && !measuresSet.has(key)) {
              measuresSet.add(key);
              this.measures.push({ id: key, name: key });
            }
          });
        }
      );

      // If no measures available, switch to TABLE view
      if (this.measures.length === 0) {
        this.changeReportVisualType('TABLE');
      }
    }
  }

  public changeReportVisualType(type: string): void {
    this.postHogService.capture(`REPORT SHELL: CHANGE VISUAL TYPE TO ${type}`);

    // Save chart model before switching away from CHART view
    if (this.reportVisualType === 'CHART' && type !== 'CHART' && this.reportVisualDataComponent) {
      this.agGridChartModel = this.reportVisualDataComponent.getChartModel();
      console.log('Saving chart model before switching to TABLE:', this.agGridChartModel);
    }

    this.reportVisualType = type;
    this.reportTemplate = {
      ...this.reportTemplate,
      parameters: {
        ...this.reportTemplate.parameters,
        reportVisualType: type,
        agGridChartModel: this.agGridChartModel, // Save chart model in template
        agGridChartType: this.agGridChartType, // Save chart type in template
      },
    };
  }

  public onChartTypeChanged(chartType: string): void {
    this.agGridChartType = chartType;
    this.updateCurrentState();
  }

  public onFilterChanged(filterModel: any): void {
    this.gridFilterModel = filterModel;
    if (this.isCanvas) {
      this.gridConfigurationsChanged.emit({
        filterModel: filterModel,
        sortModel: this.gridSortModel
      });
    }
  }

  public onSortChanged(sortModel: any): void {
    this.gridSortModel = sortModel;
    if (this.isCanvas) {
      this.gridConfigurationsChanged.emit({
        filterModel: this.gridFilterModel,
        sortModel: sortModel
      });
    }
  }

  public onChartModelChanged(chartModel: any): void {
    this.agGridChartModel = chartModel;
    if (chartModel?.chartType) {
      this.agGridChartType = chartModel.chartType;
    }
    // Do NOT call updateCurrentState here - chart emits frequently and cloneDeep + isEqual
    // on full state caused 100% CPU with Copilot open. Chart model is captured on demand
    // when saving (getReportDetails calls getChartModel()) and when switching away from CHART.

    if (this.isCanvas) {
      this.chartConfigurationsChanged.emit({
        chartModel: chartModel,
        chartType: chartModel?.chartType || this.agGridChartType,
        'ag-grid': true
      });
    }
  }

  // Filter and Data Transformation Methods
  public prepareFilterPayload(filtersArray: ReportFilter[]) {
    return filtersArray.map((filter) => {
      let parsedValues;
      let dateFormat = this.getDateFormat();
      
      if (filter.fromDate && !filter.toDate) {
        parsedValues = [this.datePipe.transform(filter.fromDate, dateFormat) as string];
      } else if (filter.fromDate && filter.toDate) {
        parsedValues = [
          this.datePipe.transform(filter.fromDate, dateFormat) as string, 
          this.datePipe.transform(filter.toDate, dateFormat) as string
        ];
      } else if (filter.operator === this.filterOperatorKeys.BETWEEN || 
        filter.operator === this.filterOperatorKeys.NOT_BETWEEN && filter.fromValue && filter.toValue) {
          const parsedFromValue = this.parseStringToArray(filter.fromValue as string);
          const parsedToValue = this.parseStringToArray(filter.toValue as string);
          parsedValues = [...parsedFromValue, ...parsedToValue];
      } else if (typeof filter.values === 'string') {
        parsedValues = this.parseStringToArray(filter.values);
      } else if (Array.isArray(filter.values)) {
        parsedValues = filter.values;
      }
      
      return {
        ...filter,
        values: parsedValues,
      };
    });
  }

  private getDateFormat(): string {
    let dateFormat = this.selectedDateTimeFormat || 'dd-MM-yyyy';
    if (this.reportDataselector && this.reportDataselector.selectedDateTimeFormat) {
      dateFormat = this.reportDataselector.selectedDateTimeFormat === 'custom' 
        ? this.reportDataselector.customDateFormat 
        : this.reportDataselector.selectedDateTimeFormat;
    }
    return dateFormat;
  }

  public parseStringToArray(input: string): number[] | string[] {
    const elements = input.split(',');
    const isNumericArray = elements.every((element) => !isNaN(Number(element.trim())));
  
    if (isNumericArray) {
      return elements.map((element) => Number(element.trim()));
    } else {
      return elements.map((element) => element.trim());
    }
  }


  // State Management Methods
  private updateCurrentState(): void {
    // Get current AG Grid chart model if in chart view
    if (this.reportVisualType === 'CHART' && this.reportVisualDataComponent) {
      this.agGridChartModel = this.reportVisualDataComponent.getChartModel();
    }

    this.currentReportState = {
      sqlFunctionMap: cloneDeep(this.sqlFunctionMap),
      filters: cloneDeep(this.appliedFilters),
      insights: Array.isArray(this.insights) ? cloneDeep(this.insights) : cloneDeep([this.insights]),
      attributes: cloneDeep(this.attributes),
      measures: cloneDeep(this.measures),
      reportVisualData: cloneDeep(this.reportVisualData),
      reportTemplate: cloneDeep(this.reportTemplate),
      agGridChartType: this.agGridChartType,
      agGridChartModel: this.agGridChartModel,
      selectedScope: cloneDeep(this.selectedScope),
      reportVisualType: this.reportVisualType,
      checkedColumns: cloneDeep(this.checkedColumns),
    };
    this.compareStates();
  }

  private compareStates(): void {
    if (!this.savedReportState) {
        this.isNewState =
        (this.currentReportState.checkedColumns && this.currentReportState.checkedColumns.length > 0) ||
        (this.currentReportState.filters && this.currentReportState.filters.length > 0) ||
        (this.currentReportState.sqlFunctionMap && Object.keys(this.currentReportState.sqlFunctionMap).length > 0);
    } else {
      this.isNewState = !isEqual(this.savedReportState, this.currentReportState);
    }
  }

  private resetReportState(): void {
    this.savedReportState = null;
    this.currentReportState = null;
    this.isNewState = false;
  }

  // Insights Methods
  public generateInsights(): void {
    if (this.insightsRequested) {
      return;
    }
    this.insightsRequested = true;
    this.loadingInsights = true;
    
    const payload = {
      reportId: this.reportId || undefined,
      answer: {
        id: "1",
        name: this.reportTemplate.name + "_Response",
        type: "ANSWER", 
        status: "OPEN",
        parameters: {
          DATA: this.reportVisualData,
          INSIGHTS: null
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: "shubhamkv1997@gmail.com",
        tags: [],
        description: null
      }
    };
      
    this.dataCopilotService.getInsights(payload).subscribe({
      next: this.handleInsightsSuccess.bind(this),
      error: this.handleInsightsError.bind(this)
    });
  }

  private handleInsightsSuccess(response: any): void {
    const newInsight = response.data.data[0].parameters.INSIGHTS;
    this.insights = [{
      content: newInsight,
      timestamp: new Date()
    }, ...this.insights.map(insight => {
      return typeof insight === 'string' 
        ? { content: insight, timestamp: new Date() }
        : insight;
    })];

    this.currentReportState.insights = this.insights;
    this.loadingInsights = false;
    this.insightsRequested = false;
    
    // Emit to add in free-form editor
    this.insightsGenerated.emit({
      reportName: this.reportTemplate?.name || 'Report',
      insights: newInsight
    });
    
    this.cdr.detectChanges();
  }

  private handleInsightsError(error: any): void {
    console.error('Error generating insights:', error);
    this.insights.unshift('Failed to generate insights. Please try again.');
    this.loadingInsights = false;
    this.insightsRequested = false;
    const errorMessage = error?.message || error?.error?.message || 'Failed to generate insights. Please try again.';
    this.insightsError.emit(errorMessage);
  }

  private resetInsights(): void {
    this.insights = [];
    this.insightsRequested = false;
  }

  // UI Utility Methods
  public toggleInsights(show: boolean): void {
    this.showInsights = show;
  }

  public setNoDataReportMessage(
    value: boolean,
    message = ReportNoDataMessages.SelectScopeAndRun,
    isError: boolean = false
  ): void {
    this.reportVisualDataEmptyState = value;
    this.reportDataEmptyStateMessage = message;
    this.reportDataError = isError;
  }

  // Resize Modal Logic
  initResize(event: MouseEvent): void {
    event.preventDefault();
    this.resizing = true;
    this.initialX = event.clientX;
    this.initialWidth = this.modalWidth;

    this.resizeListenerBound = this.resize.bind(this);
    this.stopResizeListenerBound = this.stopResize.bind(this);
    document.addEventListener('mousemove', this.resizeListenerBound);
    document.addEventListener('mouseup', this.stopResizeListenerBound);
  }

  resize(event: MouseEvent): void {
    if (this.resizing) {
      const delta = this.initialX - event.clientX;
      const newWidth = this.initialWidth + delta;
      const minWidth = this.selectedAction?.clickAction === 'SETTING' ? 450 : 350;
      this.modalWidth = Math.min(Math.max(newWidth, minWidth), 1200);
    }
  }

  stopResize(event: MouseEvent): void {
    this.resizing = false;
    document.removeEventListener('mousemove', this.resizeListenerBound);
    document.removeEventListener('mouseup', this.stopResizeListenerBound);
  }

  // API Callback Methods
  public afterSaveReport(data: any): void {
    const newReportTemplate = { ...this.reportTemplate };
    newReportTemplate.name = data?.data?.updateAsset?.name;
    newReportTemplate.description = data?.data?.updateAsset?.description;
    this.reportTemplate = newReportTemplate;
  }

  public handleTemplateTypesLoadingChange(isLoading: boolean): void {
    this.templateTypesLoading = isLoading;
  }

  public updateConversations(payload: { questions: any[], answers: any[] }): void {
    if (!payload) return;

    this.conversations = {
      questions: payload.questions || [],
      answers: payload.answers || [],
    };
    
    if (this.currentReportState) {
      this.currentReportState.conversations = cloneDeep(this.conversations);
    }
    if (this.isSavedReport && this.reportId) {
      this.copilotStorageService.saveConversations(this.reportId, this.conversations);
    }
  }

  public handleCopilotQuestion(question: string): void {
    const questionAsset = {
      parameters: { INPUT_TEXT: question },
    };
    
    // Ensure arrays exist
    if (!this.conversations.questions) this.conversations.questions = [];
    if (!this.conversations.answers) this.conversations.answers = [];

    this.conversations.questions.push(questionAsset);
    
    const answerIndex = this.conversations.answers.length;
    this.conversations.answers.push({
      isLoading: true,
      text: '',
      type: '',
      timestamp: '',
      tableData: null,
    });
    
    // Force update reference for OnPush components
    this.conversations = {...this.conversations};
    
    const payload = this.getPayloadForCopilot(question);
    
    this.dataCopilotService.reportChat(payload).subscribe({
      next: (data) => {
        const answerData = data.data.find((item: any) => item.type === 'ANSWER');
        const currentTime = this.datePipe.transform(new Date(), 'MMMM d, YYYY, h:mm a');

        if (!answerData || !answerData.parameters.DATA) {
          this.conversations.answers[answerIndex] = {
            isLoading: false,
            text: `⚠️ We could not generate an answer for this question, please try again later!`,
            type: 'simple',
            timestamp: currentTime,
            tableData: null,
            warning: true,
          };
        } else {
          this.conversations.answers[answerIndex] = {
            isLoading: false,
            text: answerData.parameters.DATA,
            type: 'simple',
            timestamp: currentTime,
            tableData: null,
          };
        }
        
        // Update reference
        this.conversations = {...this.conversations};
        
        if (this.currentReportState) {
          this.currentReportState.conversations = cloneDeep(this.conversations);
        }
        
        if (this.isSavedReport && this.reportId) {
          this.copilotStorageService.saveConversations(this.reportId, this.conversations);
        }
        
        this.cdr.detectChanges();
      },
      error: (err) => {
        const currentTime = this.datePipe.transform(new Date(), 'MMMM d, YYYY, h:mm a');
        this.conversations.answers[answerIndex] = {
          isLoading: false,
          warning: true,
          text: '⚠️ We could not generate an answer for this question, please try again later!',
          type: 'simple',
          timestamp: currentTime,
          tableData: null,
        };
        this.conversations = {...this.conversations};
        this.cdr.detectChanges();
      }
    });
  }

  private getPayloadForCopilot(messageText: string): any {
    
     if (this.isSavedReport) {
      return {
        query: messageText,
        reportId: this.reportTemplate.id,
        reportData: this.reportVisualData
      };
    } else {
      return {
        query: messageText,
        reportAsset: {
          id: "0",
          name: this.reportTemplate.name || "Temporary Report",
          description: this.reportTemplate.description || "Temporary Report Analysis",
          type: "REPORT",
          status: "OPEN",
          parameters: {
            ...this.reportTemplate.parameters,
            CONVERSATION_MESSAGES: this.copilotHelperService.convertQuestionAndAnswersToConversations(this.conversations ?? {})
          },
          creator: "user@example.com"
        },
        reportData: this.reportVisualData
      };
    }
  }

  // Public Methods needed for child components
  public getSQLFunctionForColumn(columnId: string): string {
    return this.sqlFunctionMap[columnId] || '';
  }
  
  public getSelectedColumnIds(): any[] {
    if (this.reportDataselector && this.reportDataselector.checkedColumns) {
      return this.reportDataselector.checkedColumns;
    }
    return this.checkedColumns;
  }

  public onClearSelections(): void {
    this.savedColumnIds = [];
    this.sqlFunctionMap = {};
    this.appliedFilters = [];
    this.isValidSelection = false;
    this.updateCurrentState();
  }

  public onColumnSelectionChanged(checkedColumns: any): void {
    this.currentReportState.checkedColumns = cloneDeep(this.checkedColumns);
    this.isRunDisabled = false;
    this.updateCurrentState();
  }

  public openShowSqlModal(): void {
    this.postHogService.capture('REPORT SHELL: VIEW SQL');
    this.formattedSqlHtml = this.highlightSqlKeywords(this.generatedSql);
    this.initialSql = this.generatedSql;

    const rawSql = this.generatedSql;

    const formatted = format(rawSql, {
      language: 'sql',
      tabWidth: 2,
      keywordCase: 'upper'
    });

    const tempEl = document.createElement('pre');
    tempEl.innerHTML = `<code class="language-sql">${formatted}</code>`;
    hljs.highlightElement(tempEl.querySelector('code')!);
    this.formattedSqlHtml = tempEl.innerHTML;
    this.isSqlEditable = false;
    this.showSqlModal = true;
  }

  public closeShowSqlModal(): void {
    this.showSqlModal = false;
  }

  public enableSqlEditing(): void {
    this.isSqlEditable = true;
  }

  private highlightSqlKeywords(sql: string): string {
    if (!sql) return sql;

    const keywords = [
      'SELECT',
      'FROM',
      'WHERE',
      'GROUP BY',
      'ORDER BY',
      'HAVING',
      'JOIN',
      'LEFT JOIN',
      'RIGHT JOIN',
      'INNER JOIN',
      'OUTER JOIN',
      'FULL JOIN',
      'DISTINCT',
    ];
    const regex = new RegExp(`\\b(${keywords.join('|')})\\b`, 'gi');

    return sql.replace(
      regex,
      (match) => `<span class="text-[#77ABF9]">${match}</span>`
    );
  }

  public getCurrentChartModel(): any {
    if (this.reportVisualDataComponent) {
      const chartModel = this.reportVisualDataComponent.getChartModel();
      if (chartModel) {
        this.agGridChartModel = chartModel;
        this.agGridChartType = chartModel.chartType;
        return chartModel;
      }
    }
    return this.agGridChartModel;
  }
}
